import { LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DATE_PIPE_DEFAULT_OPTIONS, NgOptimizedImage } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import dayjs from 'dayjs';
import RelativeTimePlugin from 'dayjs/plugin/relativeTime';
import LocalizedFormatPlugin from 'dayjs/plugin/localizedFormat';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PrimeNgModule } from './-services-/prime-ng/prime-ng.module';
import { DropdownModule } from 'primeng/dropdown';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { SplitButtonModule } from 'primeng/splitbutton';
import { MegaMenuModule } from 'primeng/megamenu';
import { TabMenuModule } from 'primeng/tabmenu';
import { AccordionModule } from 'primeng/accordion';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { QrCodeModule } from 'ng-qrcode';
import { InputMaskModule } from 'primeng/inputmask';
import { BlockUIModule } from 'primeng/blockui';
import { ToolbarModule } from 'primeng/toolbar';
import { TagModule } from 'primeng/tag';
import { MessagesModule } from 'primeng/messages';
import { MessageModule } from 'primeng/message';
import { MessageService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';

import { provideQuillConfig } from 'ngx-quill/config';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { CommonSharedModule } from './common/shared.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthServiceModule } from './-services-/auth-framework/auth-service.module';
import { APIInterceptor } from './APIInterceptor';
import { ErrorHandlingModule } from './error-handling/error-handling.module';
import { LocalStorageService } from './utilities/local-storage.service';

import { HeaderModule } from './common/header/header.module';
import { ReportsModule } from './reports/reports.module';
import { UserManualModule } from './user-manual/user-manual.module';
import { CgtViewModule } from './cgt-view/cgt-view.module';
import { AuthConfigModule } from './auth/auth-config.module';
import { SystemAnnouncementsModule } from './system-announcements/system-announcements.module';
import { ToggleButtonModule } from 'primeng/togglebutton';

import { LoginPageComponent } from './account-mgmt/login-page/login-page.component';
import { UsernameFormComponent } from './account-mgmt/login-page/page-forms/username-form/username-form.component';
import { PasswordFormComponent } from './account-mgmt/login-page/page-forms/password-form/password-form.component';
import { MfaFormComponent } from './account-mgmt/login-page/page-forms/mfa-form/mfa-form.component';
import { SuspenseButtonComponent } from './account-mgmt/login-page/page-forms/suspense-button/suspense-button.component';
import { UserAdminPageComponent } from './account-mgmt/user-admin-page/user-admin-page/user-admin-page.component';
import { UserRolesPageComponent } from './account-mgmt/user-admin-page/roles/user-roles-page.component';
import { UserProfilePageComponent } from './account-mgmt/user-admin-page/profile/user-profile-page.component';
import { CreateUserPageComponent } from './account-mgmt/user-admin-page/new/create-user-page.component';
import { UserAdminPageContainerComponent } from './account-mgmt/user-admin-page/user-admin-page-container.component';
import { ForcePasswordChangeFormComponent } from './account-mgmt/login-page/page-forms/force-password-change-form/force-password-change-form.component';
import { BillingPageComponent } from './account-mgmt/user-admin-page/billing/billing-page.component';
import { LogoUploaderComponent } from './account-mgmt/user-admin-page/billing/-components-/logo-uploader/logo-uploader.component';
import { BillingProfileFormComponent } from './account-mgmt/user-admin-page/billing/-components-/billing-profile-form/billing-profile-form.component';
import { CreateSiteAdminPageComponent } from './account-mgmt/user-admin-page/create-site-admin/create-site-admin-page.component';
import { UserLastAccessTimeLoaderComponent } from './account-mgmt/user-admin-page/-components-/user-last-access-time-loader/user-last-access-time-loader.component';
import { TermsAndConditionsPageComponent } from './tos-pages/terms-and-conditions-page/terms-and-conditions-page.component';
import { PrivacyPolicyPageComponent } from './tos-pages/privacy-policy-page/privacy-policy-page.component';
import { TermsPagesContainerComponent } from './tos-pages/terms-pages-container/terms-pages-container.component';
import { UserAdminPageFiltersComponent } from './account-mgmt/user-admin-page/user-admin-page/-components-/user-admin-page-filters/user-admin-page-filters.component';
import { MfaControlPanelComponent } from './account-mgmt/user-admin-page/profile/mfa/mfa-control-panel/mfa-control-panel.component';
import { EnableMfaModalComponent } from './account-mgmt/user-admin-page/profile/mfa/enable-mfa-modal/enable-mfa-modal.component';
import { EnableMfaModalLoaderComponent } from './account-mgmt/user-admin-page/profile/mfa/enable-mfa-modal/enable-mfa-modal-loader/enable-mfa-modal-loader.component';
import { EnableMfaModalContentComponent } from './account-mgmt/user-admin-page/profile/mfa/enable-mfa-modal/enable-mfa-modal-content/enable-mfa-modal-content.component';
import { MfaConfirmInputgroupComponent } from './account-mgmt/user-admin-page/profile/mfa/enable-mfa-modal/enable-mfa-modal-content/mfa-confirm-inputgroup/mfa-confirm-inputgroup.component';
import { CreateESupportComponent } from './account-mgmt/user-admin-page/create-e-support/create-e-support.component';
import { ResetForgottenPasswordPageComponent } from './account-mgmt/reset-forgotten-password-page/reset-forgotten-password-page.component';
import { UapHeaderComponent } from './account-mgmt/user-admin-page/user-admin-page/-components-/uap-header/uap-header.component';
import { UserSessionsComponent } from './account-mgmt/user-admin-page/user-sessions/user-sessions.component';
import { AboutComponent } from './about/about.component';
import { TokenResolverService } from './resolver/token-resolver.service';

dayjs.extend(RelativeTimePlugin);
dayjs.extend(LocalizedFormatPlugin);

@NgModule({
  declarations: [
    AppComponent,
    LoginPageComponent,
    UsernameFormComponent,
    PasswordFormComponent,
    MfaFormComponent,
    SuspenseButtonComponent,
    UserAdminPageComponent,
    UserRolesPageComponent,
    UserProfilePageComponent,
    CreateUserPageComponent,
    UserAdminPageContainerComponent,
    ForcePasswordChangeFormComponent,
    BillingPageComponent,
    LogoUploaderComponent,
    BillingProfileFormComponent,
    CreateSiteAdminPageComponent,
    UserLastAccessTimeLoaderComponent,
    TermsAndConditionsPageComponent,
    PrivacyPolicyPageComponent,
    TermsPagesContainerComponent,
    UserAdminPageFiltersComponent,
    MfaControlPanelComponent,
    EnableMfaModalComponent,
    EnableMfaModalLoaderComponent,
    EnableMfaModalContentComponent,
    MfaConfirmInputgroupComponent,
    CreateESupportComponent,
    ResetForgottenPasswordPageComponent,
    UapHeaderComponent,
    UserSessionsComponent,
    AboutComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    PrimeNgModule,
    InputTextModule,
    FormsModule,
    DropdownModule,
    TableModule,
    TagModule,
    MessagesModule,
    CheckboxModule,
    BreadcrumbModule,
    SplitButtonModule,
    MegaMenuModule,
    TabMenuModule,
    AccordionModule,
    InputNumberModule,
    AuthServiceModule,
    RadioButtonModule,
    DialogModule,
    ReactiveFormsModule,
    TooltipModule,
    QrCodeModule,
    InputMaskModule,
    LoadingBarRouterModule,
    CommonSharedModule,
    ToolbarModule,
    ErrorHandlingModule,
    ReportsModule,
    UserManualModule,
    SystemAnnouncementsModule,
    HeaderModule,
    MessageModule,
    NgOptimizedImage,
    BlockUIModule,
    CardModule,
    InputGroupModule,
    InputGroupAddonModule,
    ToggleButtonModule,
    CgtViewModule,
    AuthConfigModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: APIInterceptor,
      multi: true
    },
    { provide: LOCALE_ID, useValue: 'en-AU' }, //replace "de-at" with your locale
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: 'shortDate' } },
    { provide: LocalStorageService, useClass: LocalStorageService }, // providedIn: 'root', can also be used],
    MessageService,
    provideHttpClient(withInterceptorsFromDi()),
    provideQuillConfig({})
  ]
})
export class AppModule {}
